import { BaseService } from "./BaseService";
import type {
  AvgCheckResponse,
  RevenueDistributionResponse,
  TotalRevenueResponse,
} from "@/types/analytics-revenue";
import type {
  RevenueTrendResponse,
  RoomUpgradeSummaryResponse,
} from "@/types/analytics-offers";
import type {
  OperationsApprovalTimeResponse,
  OperationsComplaintsResponse,
  OperationsOnHoldCanceledResponse,
  OperationsOnTimeRateResponse,
  OperationsProcessingTimeResponse,
  OperationsTotalRequestsResponse,
  OperationsUnderestimatedServicesResponse,
} from "@/types/analytics-operations";
import type {
  ReviewOverallResponse,
  ReviewOverTimeResponse,
  ReviewAnalyticsTabApi,
} from "@/types/analytics-reviews";
import type {
  TeamsOverviewResponse,
  TeamsTopEntity,
  TeamsTopResponse,
} from "@/types/analytics-teams";
import type {
  TenantExportTab,
  TenantOffersTableRow,
  TenantOperationsTableRow,
  TenantOverviewResponse,
  TenantRangeQuery,
  TenantRevenueTableRow,
  TenantReviewsTableRow,
  TenantServicesTableRow,
  TenantTeamsTableRow,
  TenantTableExportQuery,
  TenantTableQuery,
  TenantTableResponse,
} from "@/types/analytics-tenant";

class AnalyticsTenantService extends BaseService {
  private readonly basePath = "/analytics/tenant";

  private tenantGet<T>(path: string, query?: TenantTableQuery): Promise<T> {
    return this.get<T>(path, query, { skipSelectedHotelHeader: true });
  }

  private tenantGetBlob(
    path: string,
    query?: TenantTableExportQuery,
  ): Promise<Blob> {
    return this.get<Blob>(path, query, {
      skipSelectedHotelHeader: true,
      responseType: "blob",
    });
  }

  getOverview(query: TenantRangeQuery): Promise<TenantOverviewResponse> {
    return this.tenantGet(`${this.basePath}/overview`, query);
  }

  getRevenueTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantRevenueTableRow>> {
    return this.tenantGet(`${this.basePath}/revenue`, query);
  }

  getRevenueTotal(query: TenantRangeQuery): Promise<TotalRevenueResponse> {
    return this.tenantGet(`${this.basePath}/charts/revenue/total`, query);
  }

  getRevenueAvgCheck(query: TenantRangeQuery): Promise<AvgCheckResponse> {
    return this.tenantGet(`${this.basePath}/charts/revenue/avg-check`, query);
  }

  getRevenueDistribution(
    query: TenantRangeQuery,
  ): Promise<RevenueDistributionResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/revenue/distribution`,
      query,
    );
  }

  getOffersTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantOffersTableRow>> {
    return this.tenantGet(`${this.basePath}/offers`, query);
  }

  getOffersRoomUpgradeSummary(
    query: TenantRangeQuery,
  ): Promise<RoomUpgradeSummaryResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/offers/room-upgrade/summary`,
      query,
    );
  }

  getOffersRoomUpgradeRevenueTrend(
    query: TenantRangeQuery,
  ): Promise<RevenueTrendResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/offers/room-upgrade/revenue-trend`,
      query,
    );
  }

  getReviewsTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantReviewsTableRow>> {
    return this.tenantGet(`${this.basePath}/reviews`, query);
  }

  getReviewsOverall(
    query: TenantRangeQuery & { tab: ReviewAnalyticsTabApi },
  ): Promise<ReviewOverallResponse> {
    return this.tenantGet(`${this.basePath}/charts/reviews/overall`, query);
  }

  getReviewsOverTime(
    query: TenantRangeQuery & { tab: ReviewAnalyticsTabApi },
  ): Promise<ReviewOverTimeResponse> {
    return this.tenantGet(`${this.basePath}/charts/reviews/over-time`, query);
  }

  getServicesTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantServicesTableRow>> {
    return this.tenantGet(`${this.basePath}/services`, query);
  }

  getOperationsTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantOperationsTableRow>> {
    return this.tenantGet(`${this.basePath}/operations`, query);
  }

  getOperationsTotalRequests(
    query: TenantRangeQuery,
  ): Promise<OperationsTotalRequestsResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/total-requests`,
      query,
    );
  }

  getOperationsProcessingTime(
    query: TenantRangeQuery,
  ): Promise<OperationsProcessingTimeResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/processing-time`,
      query,
    );
  }

  getOperationsApprovalTime(
    query: TenantRangeQuery,
  ): Promise<OperationsApprovalTimeResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/approval-time`,
      query,
    );
  }

  getOperationsOnTimeRate(
    query: TenantRangeQuery,
  ): Promise<OperationsOnTimeRateResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/on-time-rate`,
      query,
    );
  }

  getOperationsComplaints(
    query: TenantRangeQuery,
  ): Promise<OperationsComplaintsResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/complaints`,
      query,
    );
  }

  getOperationsOnHoldCanceled(
    query: TenantRangeQuery,
  ): Promise<OperationsOnHoldCanceledResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/on-hold-canceled`,
      query,
    );
  }

  getOperationsUnderestimatedServices(
    query: TenantRangeQuery,
  ): Promise<OperationsUnderestimatedServicesResponse> {
    return this.tenantGet(
      `${this.basePath}/charts/operations/underestimated-services`,
      query,
    );
  }

  getTeamsTable(
    query: TenantTableQuery,
  ): Promise<TenantTableResponse<TenantTeamsTableRow>> {
    return this.tenantGet(`${this.basePath}/teams`, query);
  }

  getTeamsOverview(query: TenantRangeQuery): Promise<TeamsOverviewResponse> {
    return this.tenantGet(`${this.basePath}/charts/teams/overview`, query);
  }

  getTeamsTop(
    query: TenantRangeQuery & { entity: TeamsTopEntity; limit?: number },
  ): Promise<TeamsTopResponse> {
    return this.tenantGet(`${this.basePath}/charts/teams/top`, query);
  }

  exportTable(
    tab: TenantExportTab,
    query: TenantTableExportQuery,
  ): Promise<Blob> {
    return this.tenantGetBlob(`${this.basePath}/${tab}/export`, query);
  }
}

export const analyticsTenantService = new AnalyticsTenantService();
